import type { FC, ReactNode } from 'react';
import { isValidElement, useEffect, useRef } from 'react';
import FsLightbox from 'fslightbox-react';

interface LightboxProps {
  onClose?: () => void;
  onOpen?: () => void;
  slide?: number;
  sourceIndex?: number;
  sources?: Array<ReactNode> | null;
  toggler: boolean;
  type?: string;
  types?: Array<string | null | undefined>;
}

type LightboxInstanceProps = Omit<LightboxProps, 'sources'> & {
  sources: Array<ReactNode>;
};

const OPEN_CLASS = 'fslightbox-open';

/**
 * Ключ источника для сравнения наборов между рендерами: строки сравниваем
 * как есть, у кастомных источников-элементов берём key и src.
 */
const getSourceKey = (source: ReactNode): string => {
  if (typeof source === 'string' || typeof source === 'number') {
    return String(source);
  }
  if (isValidElement(source)) {
    const { src } = (source.props || {}) as { src?: string };
    return `${source.key ?? ''}~${src ?? ''}`;
  }
  return '';
};

const releaseDocument = (): void => {
  document.documentElement.classList.remove(OPEN_CLASS);
  document.body.style.removeProperty('margin-right');
};

const LightboxInstance: FC<LightboxInstanceProps> = ({
  onClose,
  onOpen,
  ...restProps
}) => {
  const isOpenRef = useRef(false);

  useEffect(
    () => () => {
      if (isOpenRef.current) releaseDocument();
    },
    []
  );

  return (
    <FsLightbox
      {...restProps}
      onOpen={() => {
        isOpenRef.current = true;
        onOpen?.();
      }}
      onClose={() => {
        isOpenRef.current = false;
        onClose?.();
      }}
    />
  );
};

/**
 * Обёртка над FsLightbox.
 *
 * Лечит три болячки библиотеки:
 *
 * 1. Пустой или отсутствующий sources роняет её на `sources.length` — поэтому
 *    без источников не рендерим вовсе.
 * 2. Набор источников защёлкивается при монтировании: сменившийся sources
 *    библиотека не перечитывает (в componentDidUpdate обрабатываются только
 *    toggler / slide / sourceIndex). Поэтому при смене набора пересоздаём
 *    компонент по key — иначе после переключения товара или рецепта лайтбокс
 *    открывает картинки предыдущего.
 * 3. При открытии библиотека вешает на <html> класс fslightbox-open
 *    (overflow: hidden) и margin-right на body под ширину скроллбара, а
 *    снимает их только по таймеру закрытия — в componentWillUnmount ничего
 *    не убирает. Размонтирование открытого лайтбокса (назад в браузере,
 *    пересоздание по key) оставляло страницу без прокрутки.
 *
 * На версиях 2.x тот же key чинил ещё и краш «change in the order of Hooks»:
 * там useRef вызывался в цикле по sources. Проект прибит к 1.7.6, где
 * компонент классовый и хуков нет, но key оставляем — он нужен по п.2.
 */
const Lightbox: FC<LightboxProps> = ({ sources, ...restProps }) => {
  if (!sources?.length) return null;

  const sourcesKey = `${sources.length}:${sources.map(getSourceKey).join('|')}`;

  return <LightboxInstance {...restProps} key={sourcesKey} sources={sources} />;
};

export default Lightbox;
